<template>
  <div class='tid-times-picker'>
    <v-card elevation="0" color="grey lighten-5">
      <v-card-subtitle>{{ $t('route_start') }}</v-card-subtitle>
      <v-card-text>
        <date-picker-type-in
          v-model="startDate"
          :timezone="timezone"
          :output-moment="true"
          :label="$t('start_date')"
          prepend-inner-icon="mdi-calendar"
          :error-messages="startDateErrors"
          :reset-on-timezone-change="true"/>
        <time-picker
          class="flex-grow-1"
          v-model="startTime"
          :label="$t('start_time_24h')"
          format="24hr"
          :error-messages="startTimeErrors"
          prepend-inner-icon="mdi-clock"/>
      </v-card-text>
    </v-card>
    <v-card v-if="showEndPickers" elevation="0" color="grey lighten-5" class="mt-3">
      <v-card-subtitle>{{ $t('route_end') }}</v-card-subtitle>
      <v-card-text>
        <time-picker
          v-model="endTime"
          :label="$t('end_time_24h')"
          format="24hr"
          :error-messages="endTimeErrors"
          prepend-inner-icon="mdi-clock"/>
        <div v-if="projectedEndDate && !showEndDatePicker">
          <v-btn text color="primary" @click="showEndDatePicker = true">
            {{ projectedEndDate.format('LL') }}
          </v-btn>
        </div>
        <div v-if="showEndDatePicker">
          <date-picker-type-in
            v-model="endDate"
            :timezone="timezone"
            :output-moment="true"
            :label="$t('end_date')"
            :error-messages="endDateErrors"
            prepend-inner-icon="mdi-calendar"
            :reset-on-timezone-change="true" />
        </div>
      </v-card-text>
    </v-card>
    <div class="mt-2">{{ $t('timezone_with_label', { timezone: timeZoneLabel }) }}</div>
  </div>
</template>

<script>

/**
 * Component that wraps selecting the start/end dates and times. Combines the date and time components into computed
 * properties that are emitted from the component on updates.
 *
 * We avoid watching the values of the input properties to avoid some event looping issues with Moment.
 * Instead, we manually re-fire the initialize method to set up the state when the outside changes.
 * Note that there is some odd seeming logic revolving around when to show the end date picker - this has been carried
 * over from old versions of the application all the way back to the original Yii serverside version. I think the intent
 * was to speed up entry since most routes take place over a day.
 */

import DatePickerTypeIn from '@/components/shared/DatePickerTypeIn'
import TimePicker from '@/components/shared/TimePicker'
import ValidationMixin from '@/components/mixins/ValidationMixin'
import CurrentUserMixin from '@/components/mixins/CurrentUserMixin'
import Moment from 'moment-timezone'
import AppConstants from '@/AppConstants'
import { required } from 'vuelidate/lib/validators'
import { split } from 'lodash'

export default {
  name: 'RouteTimes',
  components: {
    DatePickerTypeIn,
    TimePicker
  },
  mixins: [
    ValidationMixin,
    CurrentUserMixin
  ],
  props: {
    showEnd: Boolean,
    start: Moment,
    end: Moment,
    timezone: {
      type: String,
      default: 'UTC'
    },
    editingRoute: {
      type: Boolean,
      default: false
    }
  },
  validations: {
    startDate: {
      required,
      validDate: (value, vm) => {
        return vm.startDate != null && vm.startDate.isAfter(AppConstants.APP_EPOCH)
      }
    },
    startTime: {
      required
    },
    endTime: {
      requiredIfEndDateEntered: (value, vm) => {
        if (vm.showEndDatePicker && vm.endDate != null) {
          return value != null
        }
        return true
      },
      requiredIfStartDate7DaysOld: (value, vm) => {
        const sevenDaysAgo = Moment().tz(vm.customerTimezone).subtract(Moment.duration(7, 'd'))
        if (vm.computedStart && vm.computedStart.isBefore(sevenDaysAgo)) {
          return value != null
        }
        return true
      }
    },
    endDate: {
      endDateRequiredIfShown: (value, vm) => {
        if (vm.showEndDatePicker) {
          return value != null
        }
        return true
      },
      invalidDuration: (value, vm) => {
        if (vm.showEndDatePicker) {
          if (vm.computedStart && vm.computedEnd) {
            return vm.computedStart.isBefore(vm.computedEnd)
          }
        }
        return true
      }
    }
  },
  data () {
    return {
      startDate: null,
      startTime: null,
      endDate: null,
      endTime: null,
      showEndDatePicker: null
    }
  },
  computed: {
    timeZoneLabel () {
      return Moment.tz(this.timezone).zoneAbbr()
    },
    /**
     * Get the proper start date by combining the values of the start date and start time pickers. By combining these
     * in a computed property we can hold off on emitting an update until we have a proper value here.
     * @returns {*}
     */
    computedStart () {
      const startTimeParts = split(this.startTime, ':')
      if (Moment.isMoment(this.startDate) && startTimeParts.length === 2) {
        return this.startDate
          .hours(startTimeParts[0])
          .minutes(startTimeParts[1])
          .seconds(0)
      }
      return undefined
    },
    /**
     * Similar to computedStart but for the end datetime. If end date picker is not activated, we will use the assumed
     * end value of projectedEndDate.
     * @returns {*}
     */
    computedEnd () {
      const endTimeParts = split(this.endTime, ':')
      if (endTimeParts.length === 2) {
        const endDate = this.showEndDatePicker ? this.endDate : this.projectedEndDate
        if (Moment.isMoment(endDate)) {
          return endDate
            .hours(endTimeParts[0])
            .minutes(endTimeParts[1])
            .seconds(0)
        }
      }
      return undefined
    },
    /**
     * When the end time is selected, but the end date is not, let's try to automatically compute what the end date
     * might be.
     * @returns Moment|undefined
     */
    projectedEndDate () {
      const endTimeParts = split(this.endTime, ':')
      if (Moment.isMoment(this.startDate) && endTimeParts.length === 2) {
        // Start by creating an end datetime that's on the same day as the start time
        let result = this.startDate.clone()
          .hour(endTimeParts[0])
          .minute(endTimeParts[1])
        // If the selected end time is before the selected start time then the projected end date is the start date plus
        // one day (the end should always come after the start)
        if (result.isSameOrBefore(this.computedStart)) {
          result = result.add(1, 'day')
        }
        return result
      }
      return undefined
    },

    /**
     * In general, we do not want to show the route end times for enterprise customers (as routes are ended by the
     * system.) However, in some cases, users will need to create old routes, and we don't allow users to created
     * routes older than 7 days without a route end (there's really not a case for this, and it leads to long simulated
     * temperature processing timelines).
     * Thus, if the start time is more than 7 days ago, we want to show the route end time for enterprise customers
     * (It's already shown for baseline customers.)
     *
     * @returns {boolean}
     */
    showEndPickers () {
      if (this.showEnd) {
        return true
      }
      if (this.start) {
        const sevenDaysAgo = Moment().subtract(Moment.duration(7, 'd'))
        return this.start.isBefore(sevenDaysAgo)
      }
      return false
    },

    /**
     * Error messages for the startDate field.
     * @returns {Array}
     */
    startDateErrors () {
      return this.validationErrors([{
        prop: this.$v.startDate,
        rule: 'required',
        message: this.$t('start_date_required')
      }, {
        prop: this.$v.startDate,
        rule: 'validDate',
        message: this.$t('invalid_route_date_message')
      }])
    },

    /**
     * Error messages for the startTime field.
     * @returns {Array}
     */
    startTimeErrors () {
      return this.validationErrors([{
        prop: this.$v.startTime,
        message: this.$t('start_time_required')
      }])
    },

    /**
     * Error messages for the endDate field.
     * @returns {Array}
     */
    endDateErrors () {
      return this.validationErrors([{
        prop: this.$v.endDate,
        rule: 'endDateRequiredIfShown',
        message: this.$t('end_date_required')
      }, {
        prop: this.$v.endDate,
        rule: 'invalidDuration',
        message: this.$t('route_end_date_before_start_date')
      }])
    },

    /**
     * Error messages for the endTime field.
     * @returns {Array}
     */
    endTimeErrors () {
      return this.validationErrors([{
        prop: this.$v.endTime,
        rule: 'requiredIfEndDateEntered',
        message: this.$t('end_time_required')
      }, {
        prop: this.$v.endTime,
        rule: 'requiredIfStartDate7DaysOld',
        message: this.$t('end_time_required')
      }])
    }
  },
  methods: {
    initialize () {
      if (Moment.isMoment(this.start)) {
        this.startDate = this.start.clone().startOf('day')
        this.startTime = this.start.format('HH:mm')
      } else {
        // When creating a new route, default the start date to start of current day.
        // When editing a route with NULL start, don't set a default to avoid accidental date setting.
        if (!this.editingRoute) {
          this.startDate = Moment().startOf('day').tz(this.timezone)
        } else {
          this.startDate = null
        }
        this.startTime = null
      }
      if (Moment.isMoment(this.end)) {
        this.showEndDatePicker = true
        this.endDate = this.end.clone().startOf('day')
        this.endTime = this.end.format('HH:mm')
      } else {
        this.endDate = null
        this.endTime = null
        this.showEndDatePicker = false
      }
    },
    customerContextChangeHandler() {
      this.initialize()
      this.$v.$reset()
    },
    /**
     * Reset the form back to the initial state.
     */
    reset () {
      this.initialize()
      this.$v.$reset()
    },
    validate () {
      this.$v.$touch()
      return !this.$v.$invalid
    }
  },
  watch: {
    computedStart (newValue) {
      this.$emit('update:start', newValue)
    },
    computedEnd (newValue) {
      this.$emit('update:end', newValue)
    }
  },
  mounted () {
    this.initialize()
  }
}
</script>

<style lang="scss" scoped>
</style>
